<template>
  <v-container fluid class="pa-0 fill-height">
    <!-- 🟢 Header -->
    <v-app-bar app color="primary" dark flat>
      <v-btn icon @click="$router.back()">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-toolbar-title class="font-weight-bold"
        >Profile Overview</v-toolbar-title
      >
      <v-spacer></v-spacer>
      <v-btn color="secondary" rounded small @click="editProfile">
        <v-icon left>mdi-pencil</v-icon>Edit
      </v-btn>
    </v-app-bar>

    <!-- 🟢 Profile Header -->
    <v-container class="text-center py-6">
      <v-alert
        v-if="profile.isActive === false"
        type="warning"
        colored-border
        border="left"
        prominent
        elevation="1"
        class="mx-4 mt-4"
      >
        <div class="d-flex justify-space-between align-center">
          <div>
            <v-icon left color="orange darken-2">mdi-pause-circle</v-icon>
            <span class="font-weight-bold"
              >This profile is currently paused</span
            >
            <div class="text-caption mt-1 grey--text text--darken-2">
              Your profile is hidden from matchmaking until you reactivate it.
            </div>
          </div>
          <v-btn
            small
            dark
            color="green darken-2"
            class="ml-4"
            @click="toggleProfileActive"
          >
            <v-icon left small>mdi-play-circle</v-icon> Reactivate
          </v-btn>
        </div>
      </v-alert>

      <v-alert
        v-if="profile.isActive"
        border="left"
        type="success"
        colored-border
        prominent
        elevation="1"
        class="mx-4 mt-4"
      >
        <div class="d-flex justify-space-between align-center">
          <div>
            <v-icon left color="green darken-2">mdi-check-circle</v-icon>
            <span class="font-weight-bold">Profile is Active</span>
            <div class="text-caption mt-1 grey--text text--darken-2">
              Visible to other users in matchmaking.
            </div>
          </div>
          <v-btn
            small
            dark
            color="orange darken-2"
            class="ml-4"
            @click="toggleProfileActive"
          >
            <v-icon left small>mdi-pause-circle</v-icon> Pause
          </v-btn>
        </div>
      </v-alert>
      <v-avatar size="90" class="mb-3">
        <v-img
          v-if="profile.gender === 'Female'"
          src="@/assets/avatars/wife.png"
        />
        <v-img v-else src="@/assets/avatars/brother.png" />
      </v-avatar>

      <div class="text-h5 font-weight-bold">{{ profile.name }}</div>
      <div class="text-subtitle-2 grey--text">{{ profile.gender }}</div>

      <!-- 🏷️ Status Chip -->
      <v-chip
        class="mt-2"
        small
        :color="getStatusColor(profile.approvalStatus)"
        dark
      >
        {{ formatStatus(profile.approvalStatus) }}
      </v-chip>

      <!-- 🌈 Progress Bar -->
      <v-card flat class="mt-5 mx-4">
        <v-card-text>
          <v-progress-linear
            :value="completionPercent"
            height="12"
            :color="progressColor"
            rounded
            striped
          ></v-progress-linear>
          <div class="d-flex justify-space-between align-center mt-1">
            <span class="text-caption grey--text text--darken-1">
              {{ progressMessage }}
            </span>
            <span
              class="font-weight-bold"
              :class="{
                'red--text': completionPercent < 30,
                'orange--text':
                  completionPercent >= 30 && completionPercent < 70,
                'green--text': completionPercent >= 70,
              }"
            >
              {{ completionPercent }}%
            </span>
          </div>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- 🟢 Profile Sections -->
    <v-container class="pb-10">
      <v-card
        v-for="(section, idx) in sections"
        :key="idx"
        class="mb-6"
        outlined
        rounded="xl"
      >
        <v-card-title class="font-weight-bold text--primary">
          <v-icon left color="primary">{{ section.icon }}</v-icon>
          {{ section.title }}
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text>
          <v-row>
            <v-col
              v-for="(item, index) in section.items"
              :key="index"
              cols="12"
              sm="6"
              class="py-1"
            >
              <div class="text-caption grey--text text--darken-1">
                {{ item.label }}
              </div>
              <div class="font-weight-medium text-body-2">
                {{ displayValue(item.key) }}
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- 🕊️ Reminder -->
      <div class="text-center text-caption grey--text text--darken-1 mt-8">
        <v-icon small color="primary" class="mr-1"
          >mdi-information-outline</v-icon
        >
        The more complete your profile, the better your compatibility matches
        🔍💫
      </div>
    </v-container>
  </v-container>
</template>

<script>
import profileService from "@/modules/Profile/service";

export default {
  data() {
    return {
      profile: {},
      completionPercent: 0,
      sections: [
        {
          title: "Basic Information",
          icon: "mdi-account-circle-outline",
          items: [
            { label: "Full Name", key: "name" },
            { label: "Gender", key: "gender" },
            { label: "Date of Birth", key: "dob" },
            { label: "Country", key: "country" },
            { label: "State", key: "state" },
            { label: "Marital Status", key: "maritalStatus" },
          ],
        },
        {
          title: "Physical Details",
          icon: "mdi-weight-lifter",
          items: [
            { label: "Appearance", key: "appearance" },
            { label: "Height (Feet)", key: "heightRaw" },
            { label: "Weight (lbs)", key: "weightLbs" },
            { label: "Build", key: "build" },
            { label: "Complexion", key: "complexion" },
          ],
        },
        {
          title: "Bio",
          icon: "mdi-note-outline",
          items: [{ label: "Bio", key: "bio" }],
        },
        {
          title: "Ethnicity & Origin",
          icon: "mdi-earth",
          items: [
            { label: "Ethnicity", key: "ethnicity" },
            { label: "Inner Caste / Community", key: "innerCaste" },
          ],
        },
        {
          title: "Faith & Religious Practice",
          icon: "mdi-mosque-outline",
          items: [
            { label: "Madhab (School of Thought)", key: "madhab" },
            { label: "Prayer Habit", key: "prayerFrequency" },
            { label: "Prays in Jamaat", key: "praysInJamaat" },
            { label: "Fasts in Ramadan", key: "fastInRamadan" },
            { label: "Gives Zakat", key: "givesZakat" },
            { label: "Masjid Name", key: "masjidName" },
            { label: "Masjid Address", key: "masjidAddress" },
            { label: "Islamic Education Level", key: "islamicEducationLevel" },
            { label: "Hajj Status", key: "hajjStatus" },
            { label: "Umrah Status", key: "umrahStatus" },
          ],
        },
        {
          title: "Lifestyle & Personality",
          icon: "mdi-heart-outline",
          items: [
            { label: "Personality Type", key: "personalityType" },
            { label: "Diet Type", key: "dietType" },
            { label: "Living Preference", key: "livingPreference" },
            { label: "Enjoys Hosting Guests", key: "hostGuests" },
            { label: "Likes to Travel", key: "travelLover" },
            { label: "Home Atmosphere", key: "homeAtmosphere" },
            { label: "Cooking Skill", key: "cookingSkill" },
            { label: "Pre-Marriage Counseling", key: "preMarriageCounseling" },
          ],
        },
        {
          title: "Education & Career",
          icon: "mdi-school-outline",
          items: [
            { label: "Education Level", key: "educationLevel" },
            { label: "Legal Status", key: "legalStatus" },
            { label: "Occupation Type", key: "occupationType" },
            { label: "Profession", key: "profession" },
            { label: "Employment Status", key: "employmentStatus" },
            { label: "Work Environment", key: "workEnvironment" },
            { label: "Travel for Work", key: "travelForWork" },
            { label: "Can Drive", key: "canDrive" },
            { label: "Owns Car", key: "hasCar" },
            { label: "Vehicle Type", key: "vehicleType" },
          ],
        },
        {
          title: "Family & Marriage",
          icon: "mdi-account-group-outline",
          items: [
            { label: "Has Children", key: "hasChildren" },
            { label: "Wants Children", key: "wantsChildren" },
            { label: "Father Alive", key: "fatherAlive" },
            { label: "Father Location", key: "fatherLocation" },
            { label: "Mother Alive", key: "motherAlive" },
            { label: "Mother Location", key: "motherLocation" },
            { label: "Has Siblings", key: "hasSiblings" },
            { label: "Number of Brothers", key: "brotherCount" },
            { label: "Number of Sisters", key: "sisterCount" },
            { label: "Sibling Order", key: "siblingOrder" },
            { label: "Living Arrangement", key: "livingArrangement" },
          ],
        },
        {
          title: "Marriage Expectations",
          icon: "mdi-ring",
          items: [
            { label: "Mehr Considered", key: "mehrConsidered" },
            { label: "Mehr Amount / Form", key: "mehrAmount" },
            { label: "Mehr Notes", key: "mehrNotes" },
            { label: "Wife Work Preference", key: "wifeWorkPreference" },
            {
              label: "Financial Responsibility",
              key: "financialResponsibility",
            },
            { label: "Domestic Contribution", key: "domesticContribution" },
            { label: "Supports Education", key: "supportsEducation" },
            { label: "Post-Marriage Work", key: "postMarriageWork" },
          ],
        },
        {
          title: "Health & Habits",
          icon: "mdi-heart-pulse",
          items: [
            { label: "Has Disability", key: "hasDisability" },
            { label: "Disability Detail", key: "disabilityDetail" },
            { label: "Smokes", key: "smokes" },
            { label: "Drinks", key: "drinks" },
          ],
        },
      ],
    };
  },
  computed: {
    progressColor() {
      if (this.completionPercent < 30) return "error";
      if (this.completionPercent < 70) return "warning";
      return "success";
    },
    progressMessage() {
      if (this.completionPercent < 25)
        return "😅 Add more info — matches can’t read your mind yet!";
      if (this.completionPercent < 50)
        return "🧐 Getting there! The more you share, the better the match.";
      if (this.completionPercent < 75)
        return "👍 Great effort! You're building a complete story.";
      if (this.completionPercent < 100)
        return "✨ Almost perfect — your future match will appreciate this!";
      return "💯 Excellent! You're 100% match-ready!";
    },
  },
  async created() {
    try {
      const id =
        this.$route.params.profileId ||
        this.profileId ||
        sessionStorage.getItem("lastProfileId");

      if (!id) {
        this.$swal({
          icon: "error",
          title: "Profile Not Found",
          text: "Please select a valid profile.",
          confirmButtonText: "Go Back",
        }).then(() => {
          this.$router.push({ path: '/' });
        });
        return;
      }

      sessionStorage.setItem("lastProfileId", id);
      const result = await profileService.getById(id);
      this.profile = result.data || result;
      this.calculateCompletion();
      console.log("this.profile", this.profile);
    } catch (error) {
      console.error("Error loading profile", error);
      this.$swal({
        icon: "error",
        title: "Unable to Load Profile",
        text: "This profile could not be found or may have been removed.",
        confirmButtonText: "Back to List",
      }).then(() => {
        this.$router.push({ name: "profile-list" });
      });
    }
  },
  methods: {
    async toggleProfileActive() {
      try {
        const confirm = await this.$swal({
          title: this.profile.isActive
            ? "Pause this profile?"
            : "Reactivate this profile?",
          text: this.profile.isActive
            ? "It will be hidden from others until you reactivate it."
            : "It will be visible again for matchmaking.",
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "Yes, continue",
        });

        if (!confirm.isConfirmed) return;

        const result = await profileService.toggleActive(this.profile.id, {
          isActive: !this.profile.isActive,
        });
        this.profile.isActive = !this.profile.isActive;

        this.$swal({
          icon: "success",
          title: this.profile.isActive
            ? "Profile reactivated!"
            : "Profile paused!",
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error(error);
        this.$swal({
          icon: "error",
          title: "Unable to update profile status",
        });
      }
    },
    displayValue(key) {
      const val = this.profile[key];

      if (!val || val === "unspecified" || val === "") return "—";

      // ✅ Format yes/no answers
      const yesKeys = [
        "hasChildren",
        "wantsChildren",
        "praysInJamaat",
        "givesZakat",
        "hasSiblings",
        "fatherAlive",
        "motherAlive",
        "travelLover",
        "hostGuests",
        "travelForWork",
        "fastInRamadan",
        "canDrive",
        "hasCar",
        "hasMotorbike",
        "hasDisability",
        "smokes",
        "preMarriageCounseling",
        "mehrConsidered",
        "drinks",
        "supportsEducation",
      ];

      if (yesKeys.includes(key)) {
        if (val.toLowerCase() === "yes") return "✅ Yes";
        if (val.toLowerCase() === "no") return "❌ No";
      }

      // ✅ Format Date of Birth
      if (key === "dob") {
        const d = new Date(val);
        if (isNaN(d)) return val;

        const today = new Date();
        let age = today.getFullYear() - d.getFullYear();
        const m = today.getMonth() - d.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;

        const formatted = d.toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        });

        return `${formatted} (${age} years old)`;
      }

      return val;
    },
    calculateCompletion() {
      const total = Object.keys(this.profile || {}).length || 1;
      const answered = Object.values(this.profile).filter(
        (v) => v !== null && v !== "" && v !== "unspecified"
      ).length;
      this.completionPercent = Math.round((answered / total) * 100);
    },
    getStatusColor(status) {
      switch ((status || "").toLowerCase()) {
        case "approved":
          return "green darken-1";
        case "rejected":
          return "red darken-2";
        default:
          return "amber darken-2";
      }
    },
    formatStatus(status) {
      if (!status) return "Pending";
      return status.charAt(0).toUpperCase() + status.slice(1);
    },
    editProfile() {
      this.$router.push({
        name: "profile-edit",
        params: { profileId: this.profile.id },
      });
    },
  },
};
</script>

<style scoped>
.v-avatar {
  border: 3px solid #2a6465;
}
.v-card {
  background-color: #ffffff;
}
.v-card-title {
  font-size: 16px;
}
.v-card-text {
  font-size: 14px;
}
.v-progress-linear {
  transition: 0.4s ease-in-out;
}
@media (max-width: 600px) {
  .v-card-title {
    font-size: 15px;
  }
  .v-card-text {
    font-size: 13px;
  }
}
</style>